import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  BellIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon,
  MegaphoneIcon,
  ArrowLeftIcon,
  ExclamationCircleIcon,
  TrashIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import useStatusBarHeight from '../hooks/useStatusBarHeight';

const NotificationList = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState({ grouped: [], others: [] });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const statusBarHeight = useStatusBarHeight();

  // 알림 아이콘 매핑
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return <ChatBubbleLeftRightIcon className="h-5 w-5" />;
      case 'project_status':
      case 'project_assigned':
      case 'project_created':
        return <ClipboardDocumentCheckIcon className="h-5 w-5" />;
      case 'reward':
        return <CurrencyDollarIcon className="h-5 w-5" />;
      case 'notice':
        return <MegaphoneIcon className="h-5 w-5" />;
      default:
        return <BellIcon className="h-5 w-5" />;
    }
  };

  // 알림 타입별 색상
  const getNotificationColor = (type) => {
    switch (type) {
      case 'message':
        return 'text-blue-600 bg-blue-50';
      case 'project_status':
        return 'text-purple-600 bg-purple-50';
      case 'project_assigned':
        return 'text-green-600 bg-green-50';
      case 'project_created':
        return 'text-orange-600 bg-orange-50';
      case 'reward':
        return 'text-yellow-600 bg-yellow-50';
      case 'notice':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  // 알림 목록 조회
  const fetchNotifications = async (page = 1, append = false) => {
    if (!append) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = {
        limit: 20,
        page: page
      };
      
      // 필터 적용
      if (filter === 'unread') {
        params.unread_only = true;
      } else if (filter === 'read') {
        params.read_only = true;
      }
      
      const response = await api.get('/notifications', { params });
      
      if (append) {
        // 기존 데이터에 추가
        setNotifications(prev => ({
          grouped: [...prev.grouped, ...response.data.grouped],
          others: [...prev.others, ...response.data.others]
        }));
      } else {
        // 새로운 데이터로 교체
        setNotifications(response.data);
      }
      
      setHasMore(response.data.hasMore || false);
      setTotalCount(response.data.totalCount || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Fetch notifications error:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 알림 클릭 처리
  const handleNotificationClick = async (notification) => {
    // 읽지 않은 알림이면 읽음 처리
    if (!notification.is_read) {
      try {
        await api.post('/notifications/mark-read', {
          notificationIds: [notification.id]
        });
        
        // 로컬 상태 업데이트
        setNotifications(prev => {
          const newGrouped = prev.grouped.map(group => ({
            ...group,
            notifications: group.notifications.map(n => 
              n.id === notification.id ? { ...n, is_read: true } : n
            )
          }));
          
          const newOthers = prev.others.map(n => 
            n.id === notification.id ? { ...n, is_read: true } : n
          );
          
          return {
            ...prev,
            grouped: newGrouped,
            others: newOthers,
            unreadCount: Math.max(0, (prev.unreadCount || 0) - 1)
          };
        });
      } catch (error) {
        console.error('Mark as read error:', error);
      }
    }

    // URL로 이동
    if (notification.project_id) {
      // 메시지 알림인 경우 채팅 탭으로 직접 이동
      if (notification.type === 'message' || notification.type === 'message_received' || notification.type === 'new_chat_message') {
        navigate(`/projects/${notification.project_id}?tab=chat`);
      } else {
        navigate(`/projects/${notification.project_id}`);
      }
    } else if (notification.url && notification.url.startsWith('/')) {
      navigate(notification.url);
    }
  };

  // 선택된 알림 읽음 처리
  const markSelectedAsRead = async () => {
    if (selectedIds.size === 0) return;

    try {
      await api.post('/notifications/mark-read', {
        notificationIds: Array.from(selectedIds)
      });
      setSelectedIds(new Set());
      fetchNotifications();
    } catch (error) {
      console.error('Mark selected as read error:', error);
    }
  };

  // 선택된 알림 삭제
  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;

    if (!window.confirm('선택한 알림을 삭제하시겠습니까?')) return;

    try {
      for (const id of selectedIds) {
        await api.delete(`/notifications/${id}`);
      }
      setSelectedIds(new Set());
      fetchNotifications();
    } catch (error) {
      console.error('Delete selected error:', error);
    }
  };

  // 전체 선택/해제
  const toggleSelectAll = () => {
    const allIds = new Set();
    
    notifications.grouped.forEach(group => {
      group.notifications.forEach(n => {
        if (filter === 'all' || 
            (filter === 'unread' && !n.is_read) ||
            (filter === 'read' && n.is_read)) {
          allIds.add(n.id);
        }
      });
    });
    
    notifications.others.forEach(n => {
      if (filter === 'all' || 
          (filter === 'unread' && !n.is_read) ||
          (filter === 'read' && n.is_read)) {
        allIds.add(n.id);
      }
    });

    if (selectedIds.size === allIds.size) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(allIds);
    }
  };

  // 개별 선택 토글
  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };
  
  // 프로젝트 그룹 일괄 읽음 처리
  const markGroupAsRead = async (group) => {
    const unreadIds = group.notifications
      .filter(n => !n.is_read)
      .map(n => n.id);
    
    if (unreadIds.length === 0) return;
    
    try {
      await api.post('/notifications/mark-read', {
        notificationIds: unreadIds
      });
      
      // 로컬 상태 업데이트
      setNotifications(prev => {
        const newGrouped = prev.grouped.map(g => {
          if (g.project.id === group.project.id) {
            return {
              ...g,
              notifications: g.notifications.map(n => 
                unreadIds.includes(n.id) ? { ...n, is_read: true } : n
              )
            };
          }
          return g;
        });
        
        return {
          ...prev,
          grouped: newGrouped
        };
      });
    } catch (error) {
      console.error('Mark group as read error:', error);
    }
  };

  // 컴포넌트 마운트 시 알림 조회
  useEffect(() => {
    fetchNotifications(1, false);
  }, []);
  
  // 필터 변경 시 재조회
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds(new Set());
    fetchNotifications(1, false);
  }, [filter]);
  
  // 무한 스크롤 처리
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;
      
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      
      // 하단에서 200px 이내로 접근하면 다음 페이지 로드
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        fetchNotifications(currentPage + 1, true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage, loadingMore, hasMore]);

  const hasNotifications = notifications.grouped.length > 0 || notifications.others.length > 0;
  const unreadCount = notifications.unreadCount || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white shadow-sm sticky z-20" style={{ top: `${statusBarHeight}px` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold">전체 알림</h1>
            </div>
            
            {hasNotifications && (
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleSelectAll}
                  className="text-xs sm:text-sm text-gray-600 hover:text-gray-900"
                >
                  {selectedIds.size > 0 ? '선택 해제' : '전체 선택'}
                </button>
                
                {selectedIds.size > 0 && (
                  <>
                    <button
                      onClick={markSelectedAsRead}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                      title="읽음 처리"
                    >
                      <CheckIcon className="h-5 w-5 text-green-600" />
                    </button>
                    <button
                      onClick={deleteSelected}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                      title="삭제"
                    >
                      <TrashIcon className="h-5 w-5 text-red-600" />
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 필터 */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 border-b bg-white">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              전체
              {totalCount > 0 && (
                <span className="ml-1">({totalCount})</span>
              )}
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'unread'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              안읽음
              {unreadCount > 0 && (
                <span className="ml-1">({unreadCount})</span>
              )}
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'read'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              읽음
            </button>
          </div>
          
          {hasNotifications && (
            <button
              onClick={() => fetchNotifications(1, false)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              새로고침
            </button>
          )}
        </div>
      </div>

      {/* 알림 목록 */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : !hasNotifications ? (
          <div className="text-center py-12">
            <ExclamationCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {filter === 'unread' ? '읽지 않은 알림이 없습니다' : 
               filter === 'read' ? '읽은 알림이 없습니다' : '알림이 없습니다'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* 프로젝트별 그룹화된 알림 */}
            {notifications.grouped.map((group, idx) => (
              <div key={idx} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ClipboardDocumentCheckIcon className="h-5 w-5 text-gray-600" />
                      <h3 className="font-semibold text-gray-900">
                        {group.project.title}
                      </h3>
                      <span className="text-sm text-gray-500">
                        ({group.notifications.length}개)
                      </span>
                    </div>
                    
                    {/* 그룹 일괄 읽음 버튼 */}
                    {group.notifications.some(n => !n.is_read) && (
                      <button
                        onClick={() => markGroupAsRead(group)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        모두 읽음
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {group.notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 hover:bg-gray-50 cursor-pointer ${
                        !notification.is_read ? 'bg-blue-50/30' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(notification.id)}
                          onChange={() => toggleSelect(notification.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 rounded border-gray-300 text-blue-600"
                        />
                        
                        <div
                          className="flex-1"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${getNotificationColor(notification.type)}`}>
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm ${notification.is_read ? 'text-gray-600' : 'text-gray-900 font-semibold'}`}>
                                {notification.content}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {format(new Date(notification.created_at), 'yyyy년 M월 d일 HH:mm', { locale: ko })}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* 기타 알림 */}
            {notifications.others.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900">기타 알림</h3>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {notifications.others.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 hover:bg-gray-50 cursor-pointer ${
                        !notification.is_read ? 'bg-blue-50/30' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(notification.id)}
                          onChange={() => toggleSelect(notification.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 rounded border-gray-300 text-blue-600"
                        />
                        
                        <div
                          className="flex-1"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${getNotificationColor(notification.type)}`}>
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm ${notification.is_read ? 'text-gray-600' : 'text-gray-900 font-semibold'}`}>
                                {notification.content}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {format(new Date(notification.created_at), 'yyyy년 M월 d일 HH:mm', { locale: ko })}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* 로딩 인디케이터 */}
            {loadingMore && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-sm text-gray-600">더 불러오는 중...</span>
              </div>
            )}
            
            {/* 모든 알림을 불러온 경우 */}
            {!hasMore && hasNotifications && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">모든 알림을 불러왔습니다</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationList;